import React, { useContext, useState, useEffect } from "react";
import { UserContext } from "../../CheckoutPage";
// Remove pincode API import since pincode is now in Student Details
import ReactGA from "react-ga4";
import { trackData } from "../../../../Utils/attribution/CheckoutAttribution";
import "./V2DeliveryAddress.css";

const V2DeliveryAddress: React.FC = () => {
  const userContext = useContext(UserContext);
  const userDetails = userContext?.userDetails ?? {
    name: "",
    email: "",
    school: "",
    schoolID: 0,
    grade: 0,
    mobileNumber: "",
    address: "",
    flatNo: "",
    city: "",
    state: "",
    pincode: "",
  };
  const setUserDetails = userContext?.setUserDetails ?? (() => {});

  const [address, setAddress] = useState(userDetails.address || "");
  const [flatNo, setFlatNo] = useState(userDetails.flatNo || "");
  const [city, setCity] = useState(userDetails.city || "");
  const [state, setState] = useState(userDetails.state || "");
  // Remove pincode state since it's now in Student Details
  // const [pincode, setPincode] = useState(userDetails.pincode || "");
  // const [loading, setLoading] = useState(false);

  // Advanced Analytics Tracking - from original FormCluster
  const [trackedFields, setTrackedFields] = useState<Record<string, boolean>>(
    () => {
      const saved = sessionStorage.getItem("trackedAddressFields");
      return saved
        ? JSON.parse(saved)
        : {
            // Remove pincode tracking since it's now in Student Details
            // pincode: false,
            city: false,
            state: false,
            address: false,
            flatNo: false,
          };
    }
  );

  // Save tracking data to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      "trackedAddressFields",
      JSON.stringify(trackedFields)
    );
  }, [trackedFields]);

  // Sync local state with userDetails context when city/state are updated by pincode
  useEffect(() => {
    if (userDetails.city && userDetails.city !== city) {
      setCity(userDetails.city);
    }
    if (userDetails.state && userDetails.state !== state) {
      setState(userDetails.state);
    }
  }, [userDetails.city, userDetails.state, city, state]);

  // Analytics tracking function - matching original FormCluster
  const trackFieldCompletion = (fieldName: string, isValid: boolean) => {
    if (isValid && !trackedFields[fieldName]) {
      trackData(`5678_checkout_${fieldName}`);
      ReactGA.event({
        category: "Form_Completion",
        action: `5678_checkout_${fieldName}`,
        label: "V2_Delivery_Address",
      });
      setTrackedFields((prev) => ({
        ...prev,
        [fieldName]: true,
      }));
    }
  };

  // Remove address fetching function since pincode is now in Student Details
  // const fetchAddressFromPincode = async (pincodeValue: string) => { ... };

  const handleInputChange = (field: string, value: string) => {
    switch (field) {
      case "address":
        setAddress(value);
        setUserDetails({ ...userDetails, address: value });
        // Track address completion (removed length validation)
        if (value.length > 0) {
          trackFieldCompletion("address", true);
        }
        break;
      case "flatNo":
        setFlatNo(value);
        setUserDetails({ ...userDetails, flatNo: value });
        // Track flat number completion (removed length validation)
        if (value.length > 0) {
          trackFieldCompletion("flatNo", true);
        }
        break;
      case "city":
        setCity(value);
        setUserDetails({ ...userDetails, city: value });
        // Track city completion
        if (value.length >= 2) {
          trackFieldCompletion("city", true);
        }
        break;
      case "state":
        setState(value);
        setUserDetails({ ...userDetails, state: value });
        // Track state completion
        if (value.length >= 2) {
          trackFieldCompletion("state", true);
        }
        break;
      // Remove pincode case since it's now in Student Details
      // case "pincode": { ... }
    }
  };

  return (
    <div className="v2-section">
      <div className="v2-section-header">
        <div className="v2-section-number">2</div>
        <h2 className="v2-section-title">Delivery Address</h2>
        <span className="v2-section-subtitle">
          (Product will be delivered to this address)
        </span>
      </div>

      <div className="v2-form-grid">
        <div className="v2-form-group">
          <label className="v2-label">Flat No/ House No</label>
          <input
            type="text"
            className="v2-input"
            value={flatNo}
            onChange={(e) => handleInputChange("flatNo", e.target.value)}
            placeholder="203, Near laravel"
          />
        </div>

        <div className="v2-form-group">
          <label className="v2-label">Address</label>
          <input
            type="text"
            className="v2-input"
            value={address}
            onChange={(e) => handleInputChange("address", e.target.value)}
            placeholder="New apartment"
          />
        </div>

        <div className="v2-form-group">
          <label className="v2-label">City*</label>
          <input
            type="text"
            className="v2-input"
            value={city}
            onChange={(e) => handleInputChange("city", e.target.value)}
            placeholder="Bengaluru"
          />
        </div>

        <div className="v2-form-group">
          <label className="v2-label">State</label>
          <select
            className="v2-select"
            value={state}
            onChange={(e) => handleInputChange("state", e.target.value)}
          >
            <option value="">Select State</option>
            <option value="KARNATAKA">Karnataka</option>
            <option value="MAHARASHTRA">Maharashtra</option>
            <option value="TAMIL NADU">Tamil Nadu</option>
            <option value="ANDHRA PRADESH">Andhra Pradesh</option>
            <option value="KERALA">Kerala</option>
            <option value="TELANGANA">Telangana</option>
            <option value="WEST BENGAL">West Bengal</option>
            <option value="GUJARAT">Gujarat</option>
            <option value="RAJASTHAN">Rajasthan</option>
            <option value="PUNJAB">Punjab</option>
            <option value="HARYANA">Haryana</option>
            <option value="MADHYA PRADESH">Madhya Pradesh</option>
            <option value="UTTAR PRADESH">Uttar Pradesh</option>
            <option value="BIHAR">Bihar</option>
            <option value="ODISHA">Odisha</option>
            <option value="ASSAM">Assam</option>
            <option value="JHARKHAND">Jharkhand</option>
            <option value="CHHATTISGARH">Chhattisgarh</option>
            <option value="HIMACHAL PRADESH">Himachal Pradesh</option>
            <option value="UTTARAKHAND">Uttarakhand</option>
            <option value="GOA">Goa</option>
            <option value="DELHI">Delhi</option>
            <option value="JAMMU AND KASHMIR">Jammu and Kashmir</option>
            <option value="LADAKH">Ladakh</option>
            <option value="CHANDIGARH">Chandigarh</option>
            <option value="PUDUCHERRY">Puducherry</option>
            <option value="ANDAMAN AND NICOBAR ISLANDS">
              Andaman and Nicobar Islands
            </option>
            <option value="DADRA AND NAGAR HAVELI AND DAMAN AND DIU">
              Dadra and Nagar Haveli and Daman and Diu
            </option>
            <option value="LAKSHADWEEP">Lakshadweep</option>
            <option value="NCT OF DELHI">NCT of Delhi</option>
          </select>
        </div>

        {/* Remove pincode field since it's now in Student Details */}
        {/* <div className="v2-form-group">
          <label className="v2-label">Pincode *</label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              className="v2-input"
              value={pincode}
              disabled={loading}
              onChange={(e) => handleInputChange("pincode", e.target.value)}
              placeholder="Enter pincode"
              maxLength={6}
            />
            {loading && (
              <div
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <div className="v2-loading-spinner"></div>
                <span style={{ fontSize: "12px", color: "#666" }}>
                  Fetching...
                </span>
              </div>
            )}
          </div>
          {loading && (
            <div className="v2-loading-text" style={{ marginTop: "5px" }}>
              <span>Looking up city and state</span>
              <div className="v2-loading-dot">.</div>
              <div className="v2-loading-dot">.</div>
              <div className="v2-loading-dot">.</div>
            </div>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default V2DeliveryAddress;
